<template>
    <div v-if="attr" ref="wrap" class="atg-lanes" :class="{ 'atg-lanes-multi': isMulti }">
        <template v-if="!ready"></template>
        <template v-else-if="isMulti">
            <template v-for="(la, i) in laneAttrs" :key="la.html_id">
                <div class="atg-lane" :style="laneStyleObj(i)">
                    <AttrGrid :widget_id="widget_id" :initialAttr="la"
                        :ref="el => { laneRefs[i] = el }" />
                </div>
                <div v-if="i < laneAttrs.length - 1" class="atg-splitter"
                    :style="{ '--atg-split-inset': splitInset + 'px' }"
                    @pointerdown="splitterDown($event, i)"></div>
            </template>
        </template>
        <AttrGrid v-else :widget_id="widget_id" :attr_id="attr_id"
            :initialAttr="initialAttr" :parentAttr="parentAttr" />
    </div>
</template>

<script>
// ---------------------------------------------------------------------------
// AttrTabGridLanes — «МУЛЬТИ ГОРИЗОНТАЛЬНАЯ таблица» (multi horizontal table):
// координатор многоколоночного AttrTabGrid (columnsLayout), полосы секций
// side-by-side. Вертикальный родственник — AttrGridGroup (печать:
// стопка секций со своими ширинами колонок).
//
// Гибрид по проекту multi-column-attrtabgrid-proposal.md: ОДИН логический
// список секций (attr.attrs), K физических AttrGrid-полос по срезам списка.
// Резка ТОЛЬКО по границам секций верхнего уровня (row-label группы) — секция
// никогда не рвётся между полосами. При узкой ширине K=1 — рендерится ровно
// сегодняшний одиночный AttrGrid с тем же attr_id (нулевая регрессия).
//
// Выбранная форма (низкорисковая, из согласованного плана): K sibling
// AttrGrid-инстансов над срезами ОДНОГО списка — нативные collapse/selection
// per lane; тонкая координация: общий поиск (общее имя attr → общие CHIPS в
// viewParams), стрелки через границы полос, командная панель — у первой полосы.
// ---------------------------------------------------------------------------
import { useMainStore } from '@/stores/mainStore'
import * as wjcCore from '@mescius/wijmo'
import * as wjGrid from '@mescius/wijmo.grid'

// --- Фаза 1: чистый движок распределения секций по полосам -------------------
// sections — attrs верхнего уровня AttrTabGrid (секция-группа или одиночное поле),
// layout — columnsLayout {maxColumns, minColumnWidth, breakAtGroups, maxRows, balance},
// widthPx — доступная ширина. Возвращает массив полос (массивов секций).
export function distribute_lanes(sections, layout = {}, widthPx = 0) {
    sections = Array.isArray(sections) ? sections.filter(s => s && s.use !== false) : []
    const minW = layout.minColumnWidth || 340
    const maxK = Math.max(1, layout.maxColumns || 2)
    const K = Math.min(maxK, Math.max(1, Math.floor((widthPx || 0) / minW)))
    if (K <= 1 || sections.length < 2) return [sections]

    const weight = (a) => { // оценка высоты секции = число строк (рекурсивно)
        let n = 1
        for (const c of a?.attrs || []) n += weight(c)
        return n
    }
    // блоки: секции, связанные keepWithNext / columnBreakAvoid, не разрываем
    const blocks = []
    let cur = null
    for (const s of sections) {
        if (cur && (cur.keepNext || s.columnBreakAvoid) && !s.columnBreakBefore) {
            cur.items.push(s); cur.w += weight(s)
        } else {
            cur = { items: [s], w: weight(s), breakBefore: !!s.columnBreakBefore, keepNext: false }
            blocks.push(cur)
        }
        cur.keepNext = !!s.keepWithNext
    }
    const total = blocks.reduce((t, b) => t + b.w, 0)
    const maxRows = layout.maxRows || Infinity
    const orderMode = layout.balance === 'order'
    const target = orderMode ? Math.min(Math.max(Math.ceil(total / K), 1), maxRows) : total / K
    const lanes = [[]]
    let laneW = 0
    for (const b of blocks) {
        const canBreak = lanes.length < K && lanes[lanes.length - 1].length > 0
        const over = orderMode
            ? laneW >= target
            : (laneW > 0 && laneW + b.w > target * 1.15) || laneW >= maxRows
        if (canBreak && (b.breakBefore || over)) {
            lanes.push([]); laneW = 0
        }
        lanes[lanes.length - 1].push(...b.items)
        laneW += b.w
    }
    return lanes.filter(l => l.length)
}

export default {
    props: {
        widget_id: [String, Number],
        attr_id: String,
        initialAttr: { type: Object, default: null },
        parentAttr: { type: Object, default: null },
    },
    data: () => ({
        WIDGET: {},
        attr: null,
        parent_attr: null,
        laneAttrs: [],       // синтетические attr-объекты полос (стабильны по индексу)
        laneRefs: [],
        laneFractions: [],   // доли ширины полос (сплиттеры), сумма ~1
        wrapWidth: 0,
        ready: false,        // первый замер ширины до маунта детей — без K=1→K churn
        splitInset: 5,       // отступ пунктира сплиттера (30% высоты строки)
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    computed: {
        isMulti() { return this.laneAttrs.length > 1 },
        // общие CHIPS полос (имя атрибута общее) — для watcher'а ниже
        sharedCHIPS() {
            if (!this.WIDGET?.record || !this.attr?.name) return null
            return this.store.get_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.CHIPS`)
        },
        // общий Show empty: переключение должно перефильтровать ВСЕ полосы
        // (иначе пустые реквизиты появлялись только после пересборки по ширине)
        sharedShowEmpty() {
            if (!this.WIDGET?.record || !this.attr?.name) return null
            return this.store.get_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.showEmpty`)
        },
    },
    watch: {
        // поиск/CHIPS: чипс создаёт AttrChips первой полосы (напрямую зовёт её
        // apply_CHIPS через @update:modelValue) — остальные полосы про изменение
        // не узнают (in-place push не триггерит их не-deep watcher 'CHIPS').
        // Координатор deep-следит за общим массивом и перефильтровывает ВСЕ полосы.
        sharedCHIPS: {
            deep: true,
            handler() {
                if (!this.isMulti) return
                // полный apply (не fast): fast пропускает строки с уже вычисленной
                // видимостью (_fe.visible) — смена чипса требует пересчёта всех строк
                for (const ref of this.laneRefs) ref?.apply_CHIPS?.(null, {})
            },
        },
        sharedShowEmpty() {
            if (!this.isMulti) return
            for (const ref of this.laneRefs) ref?.apply_CHIPS?.(null, {})
        },
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        const WORKSPACE = this.store.WORKSPACES[this.WIDGET.workspace]
        this.PAGE = WORKSPACE.PAGES[this.WIDGET.page]
        this.resolve_attr()
    },
    mounted() {
        // измерения/пересборка — вне колбэка RO (иначе 'ResizeObserver loop' в оверлее)
        this._ro = new ResizeObserver(() => {
            if (this._roRaf) cancelAnimationFrame(this._roRaf)
            this._roRaf = requestAnimationFrame(() => { this._roRaf = null; this.measure() })
        })
        if (this.$refs.wrap) this._ro.observe(this.$refs.wrap)
        this.$refs.wrap?.addEventListener('keydown', this.laneKeydown, true)
        this.measure()      // синхронно до первого рендера детей (ready=false в шаблоне)
        this.ready = true
    },
    beforeUnmount() {
        this._ro?.disconnect()
        if (this._roRaf) cancelAnimationFrame(this._roRaf)
        this.$refs.wrap?.removeEventListener('keydown', this.laneKeydown, true)
        this.store.attr_deregister(this)
    },
    methods: {
        resolve_attr() {
            if (this.initialAttr && (this.store.tabs_noId || this.attr_id == null)) {
                this.attr = this.initialAttr
                this.parent_attr = this.parentAttr
            } else {
                // без регистрации: в K=1 бродкасты должны идти напрямую дочернему
                // AttrGrid (сегодняшнее поведение); регистрируемся только в multi
                this.store.widget_attr_set(this, false)
            }
        },
        // broadcast от WidgetAttrs.content_Changed (мы зарегистрированы только в multi)
        content_Changed(command) {
            this.resolve_attr()
            // IE 2026 10 29 / ODOO-349799 (D5): бродкаст WidgetAttrs.content_Changed (ответ updateWIDGET / updateDoc — Reload Record, Unpin predefined) идёт БЕЗ command, а ресайз-пересборка — через measure(); без force полосы с теми же секциями (ссылки) пропускались и значения (Non Updatable) оставались старыми
            this.rebuild(command, true)   // IE 2026 10 29 / ODOO-349799 (D5)
        },
        measure() {
            // отступ пунктира сплиттера: 30% высоты строки сверху и снизу
            const ds = this.laneRefs?.[0]?.flex?.rows?.defaultSize
            if (ds && Math.round(ds * 0.3) !== this.splitInset) {
                this.splitInset = Math.round(ds * 0.3)
            }
            const w = this.$refs.wrap?.clientWidth || 0
            if (Math.abs(w - this.wrapWidth) < 2 && this.laneAttrs.length) return
            this.wrapWidth = w
            this.resolve_attr()
            this.rebuild()
        },
        rebuild(command, force = false) {   // IE 2026 10 29 / ODOO-349799 (D5): force — бродкаст данных (content_Changed), пересборка нужна и без command
            const attr = this.attr
            if (!attr) return
            const lanes = (attr.columnsLayout && this.wrapWidth)
                ? distribute_lanes(attr.attrs || [], attr.columnsLayout, this.wrapWidth)
                : [attr.attrs || []]
            if (lanes.length <= 1) {
                if (this.laneAttrs.length) {
                    this.laneAttrs = []
                    this.laneRefs = []
                }
                return
            }
            // ресайз без изменения РАСПРЕДЕЛЕНИЯ секций: K тот же и составы полос
            // идентичны (по ссылкам) → НЕ пересобираем полосы и не зовём content_Changed —
            // иначе каждый шаг драга полностью ре-рендерит гриды (мерцание иконок/бейджей).
            // command (данные изменились) — пересборка нужна всегда.
            if (!command && !force && this.laneAttrs.length === lanes.length   // IE 2026 10 29 / ODOO-349799 (D5): + !force
                && lanes.every((sec, i) => {
                    const prev = this.laneAttrs[i]?.attrs
                    return prev && prev.length === sec.length && prev.every((s, j) => s === sec[j])
                })) {
                return
            }
            // доли ширины: сохранённые (viewParams laneWidths, если длина совпала) или поровну
            const stored = this.store.get_viewParam(this.WIDGET, attr.name, 'laneWidths', null, attr)
            this.laneFractions = (Array.isArray(stored) && stored.length === lanes.length)
                ? stored.slice()
                : lanes.map(() => 1 / lanes.length)
            const next = []
            for (let i = 0; i < lanes.length; i++) {
                let la = this.laneAttrs[i]
                if (!la) {
                    la = { ...attr }
                    delete la.columnsLayout          // полоса — обычный AttrTabGrid
                    la.component = 'AttrGrid'
                    la.id = undefined                // не регистрируется в attrs_vueObj
                    la.html_id = `${attr.html_id || attr.name || 'atg'}_lane${i + 1}`
                    la.noResize = true
                    // name оставляем ОБЩИМ (attr.name): CHIPS-поиск в viewParams один
                    // на все полосы — поиск первой полосы фильтрует и остальные
                    if (i > 0) la.hideCommandPanel = true // одна командная панель
                }
                la.attrs = lanes[i]
                next.push(la)
            }
            this.laneRefs = this.laneRefs.slice(0, next.length)
            this.laneAttrs = next
            this.$nextTick(() => this.lanes_bindSelection())
            // multi: бродкасты content_Changed должны приходить координатору
            if (attr.id != null) this.WIDGET.attrs_vueObj[attr.id] = this
            if (!this.store.hide_console) {
                console.log(`[AttrTabGridLanes] ${attr.name}: K=${next.length} w=${this.wrapWidth}`,
                    next.map(l => l.attrs.map(a => a.title || a.template || a.binding || a.name)))
            }
            this.$nextTick(() => {
                for (const ref of this.laneRefs) ref?.content_Changed?.(command)
            })
        },
        laneStyleObj(i) {
            const fr = this.laneFractions[i] || (1 / Math.max(this.laneAttrs.length, 1))
            return { flex: `${fr} 1 0%`, minWidth: '0' }
        },
        // --- Фаза 4: сплиттеры -------------------------------------------------
        splitterDown(e, i) {
            e.preventDefault()
            const startX = e.clientX
            const fr = this.laneFractions.slice()
            const pair = (fr[i] || 0) + (fr[i + 1] || 0)
            const wrapW = this.$refs.wrap?.clientWidth || 1
            const move = (ev) => {
                const d = (ev.clientX - startX) / wrapW
                const a = Math.min(Math.max((fr[i] || 0) + d, 0.12), pair - 0.12)
                this.laneFractions[i] = a
                this.laneFractions[i + 1] = pair - a
            }
            const up = () => {
                window.removeEventListener('pointermove', move)
                window.removeEventListener('pointerup', up)
                // персист в viewParams (как другие view-параметры атрибута)
                this.store.set_viewParam(this.WIDGET, this.attr.name, 'laneWidths',
                    this.laneFractions.slice(), this.attr)
            }
            window.addEventListener('pointermove', move)
            window.addEventListener('pointerup', up)
        },
        // --- Фаза 3: стрелки через границы полос --------------------------------
        laneGridEls() {
            const wrap = this.$refs.wrap
            if (!wrap) return []
            return [...wrap.querySelectorAll(':scope > .atg-lane')]
                .map(l => l.querySelector('.wj-flexgrid'))
        },
        lanes_bindSelection() {
            // выделение видно только в ОДНОЙ полосе мульти-горизонтальной таблицы
            for (const ref of this.laneRefs || []) {
                const fx = ref?.flex
                if (!fx || fx.__laneSelBound) continue
                fx.__laneSelBound = true
                fx.selectionChanged.addHandler(() => {
                    if (this._laneSelSyncing || !fx.selection?.isValid) return
                    this._laneSelSyncing = true
                    try {
                        for (const r2 of this.laneRefs || []) {
                            const f2 = r2?.flex
                            if (f2 && f2 !== fx && f2.selection?.isValid) {
                                f2.select(new wjGrid.CellRange(-1, -1), false)
                            }
                        }
                    } finally { this._laneSelSyncing = false }
                })
            }
        },
        laneKeydown(e) {
            if (!this.isMulti) return
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
            if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return
            const els = this.laneGridEls()
            const i = els.findIndex(el => el && el.contains(e.target))
            if (i < 0) return
            const g = wjcCore.Control.getControl(els[i])
            if (!g || !g.selection) return
            const visCols = g.columns.filter(c => c.visible !== false).map(c => c.index)
            if (!visCols.length) return
            const firstCol = visCols[0], lastCol = visCols[visCols.length - 1]
            const sel = g.selection
            let target = -1, col = 0
            if (e.key === 'ArrowRight' && sel.col >= lastCol && i < els.length - 1) {
                target = i + 1; col = firstCol
            } else if (e.key === 'ArrowLeft' && sel.col <= firstCol && i > 0) {
                target = i - 1; col = lastCol
            }
            if (target < 0) return
            const gT = wjcCore.Control.getControl(els[target])
            if (!gT || !gT.rows.length) return
            e.preventDefault()
            e.stopPropagation()
            const row = Math.min(Math.max(sel.row, 0), gT.rows.length - 1)
            gT.focus()
            gT.select(new wjGrid.CellRange(row, col), true)
        },
    },
}
</script>

<style>
.atg-lanes {
    width: 100%;
}
.atg-lanes-multi {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
}
.atg-lanes-multi > .atg-lane {
    min-width: 0;
    overflow: hidden;
}
.atg-lanes-multi > .atg-splitter {
    /* как разрыв break_before: зазор 15px с точечной вертикалью по центру —
       одинаковые отступы слева/справа, без сплошных бордюров у краёв таблиц */
    flex: 0 0 15px;
    align-self: stretch;
    cursor: col-resize;
    position: relative;
    margin: 0;
}
.atg-lanes-multi > .atg-splitter::before {
    content: '';
    position: absolute;
    /* пунктир с отступами: 30% высоты первой строки сверху, 30% последней снизу */
    top: var(--atg-split-inset, 5px);
    bottom: var(--atg-split-inset, 5px);
    left: 50%;
    width: 3px;
    margin-left: -1px;
    background-image: radial-gradient(circle, #a1a3a5 1px, transparent 1px);
    background-size: 3px 3px;
}
.atg-lanes-multi > .atg-splitter:hover {
    background: var(--cs-accent, #647eff);
    opacity: 0.35;
}
</style>
